import { Copy, Eye, FileUp, MoreVertical, Pencil, Share2, Trash2 } from 'lucide-react';

import { useCompartilharNoEscritorio } from '@/components/escritorio/useCompartilharNoEscritorio';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type { PeticaoModelo } from './ModelosPage';

interface ModeloActionsMenuProps {
  modelo: PeticaoModelo;
  canWrite: (origem?: string | null, escritorioId?: string | null) => boolean;
  canShare: (origem?: string | null, escritorioId?: string | null, userId?: string | null) => boolean;
  onUsar: (modelo: PeticaoModelo) => void;
  onView: (modelo: PeticaoModelo) => void;
  onEdit: (modelo: PeticaoModelo) => void;
  onDuplicate: (modelo: PeticaoModelo) => void;
  onShare: (modelo: PeticaoModelo) => void;
  onDelete: (modelo: PeticaoModelo) => void;
  triggerClassName?: string;
  stopPropagation?: boolean;
}

export function ModeloActionsMenu({
  modelo,
  canWrite,
  canShare,
  onUsar,
  onView,
  onEdit,
  onDuplicate,
  onShare,
  onDelete,
  triggerClassName,
  stopPropagation = false,
}: ModeloActionsMenuProps) {
  // Poucos modelos por pessoa: cada menu leva o próprio diálogo (as consultas são as mesmas, em cache).
  const compartilhar = useCompartilharNoEscritorio();
  const handleStopPropagation = stopPropagation ? (e: React.MouseEvent) => e.stopPropagation() : undefined;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={triggerClassName} onClick={handleStopPropagation}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={handleStopPropagation}>
          <DropdownMenuItem onClick={() => onUsar(modelo)}>
            <FileUp className="mr-3 h-4 w-4" /> Usar modelo
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onView(modelo)}>
            <Eye className="mr-3 h-4 w-4" /> Visualizar
          </DropdownMenuItem>
          {/* Sempre: modelo que o usuário não altera é editado numa cópia dele. */}
          <DropdownMenuItem onClick={() => onEdit(modelo)}>
            <Pencil className="mr-3 h-4 w-4" /> Editar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDuplicate(modelo)}>
            <Copy className="mr-3 h-4 w-4" /> Duplicar
          </DropdownMenuItem>
          {canShare(modelo.origem, modelo.escritorio_id, modelo.usuario_id) && (
            <DropdownMenuItem onClick={() => onShare(modelo)}>
              <Share2 className="mr-3 h-4 w-4" /> Compartilhar
            </DropdownMenuItem>
          )}
          {compartilhar.podeCompartilhar && modelo.origem === 'pessoal' && (
            <DropdownMenuItem onClick={() => compartilhar.abrir('MODELO_PETICAO', modelo.id, modelo.titulo)}>
              <Share2 className="mr-3 h-4 w-4" /> Compartilhar no escritório…
            </DropdownMenuItem>
          )}
          {canWrite(modelo.origem, modelo.escritorio_id) && (
            <DropdownMenuItem onClick={() => onDelete(modelo)} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-3 h-4 w-4" /> Excluir
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* O clique no diálogo (portal) sobe pela árvore do React até o cartão, que abre o modelo. */}
      {compartilhar.dialogo && <span onClick={(e) => e.stopPropagation()}>{compartilhar.dialogo}</span>}
    </>
  );
}
